import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { Box, Paper, Typography } from '@mui/material';
import { FirebaseUsageData } from '../../services/firebaseUsageService';

// تسجيل مكونات ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// واجهة خصائص مخطط الاستخدام
interface UsageChartProps {
  title: string;
  data: FirebaseUsageData[] | any[];
  type: 'line' | 'bar' | 'pie';
  dataKey: 'storage' | 'database' | 'auth' | 'functions' | 'hosting' | string;
  subKey?: string;
  height?: number;
  yAxisLabel?: string;
  xAxisLabel?: string;
}

// تنسيق التاريخ للعرض
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('ar-EG', {
    day: '2-digit',
    month: 'short'
  }).format(date);
};

// تنسيق حجم البيانات للعرض
const formatSize = (bytes: number): string => {
  if (bytes === 0) return '0 بايت';

  const k = 1024;
  const sizes = ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت', 'تيرابايت'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// مكون مخطط الاستخدام
const UsageChart: React.FC<UsageChartProps> = ({
  title,
  data,
  type,
  dataKey,
  subKey,
  height = 300,
  yAxisLabel,
  xAxisLabel
}) => {
  // تحضير البيانات للمخطط
  const prepareChartData = () => {
    // التعامل مع البيانات المخصصة (مثل بيانات الجداول)
    if (type === 'pie' && !('date' in data[0])) {
      const labels = data.map(item => item.name || item.collection || '');
      const values = data.map(item => item[dataKey as string]);

      return {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: [
              'rgba(63, 81, 181, 0.7)',
              'rgba(156, 39, 176, 0.7)',
              'rgba(233, 30, 99, 0.7)',
              'rgba(66, 165, 245, 0.7)',
              'rgba(76, 175, 80, 0.7)',
              'rgba(255, 152, 0, 0.7)',
              'rgba(244, 67, 54, 0.7)',
            ],
            borderColor: [
              'rgba(63, 81, 181, 1)',
              'rgba(156, 39, 176, 1)',
              'rgba(233, 30, 99, 1)',
              'rgba(66, 165, 245, 1)',
              'rgba(76, 175, 80, 1)',
              'rgba(255, 152, 0, 1)',
              'rgba(244, 67, 54, 1)',
            ],
            borderWidth: 1,
          },
        ],
      };
    }

    // التعامل مع بيانات FirebaseUsageData
    const labels = data.map(item => 'date' in item ? formatDate(item.date) : '');

    // استخراج البيانات حسب المفتاح والمفتاح الفرعي
    const values = data.map(item => {
      if (subKey && dataKey in item) {
        return (item[dataKey as keyof typeof item] as any)[subKey];
      }
      return dataKey in item ? item[dataKey as keyof typeof item] : null;
    });

    // إنشاء مجموعات البيانات حسب نوع المخطط
    if (type === 'pie' && typeof values[0] === 'object') {
      // للمخطط الدائري، نستخدم آخر قيمة فقط
      const lastValue = values[values.length - 1];
      const pieLabels = Object.keys(lastValue);
      const pieData = pieLabels.map(key => (lastValue as any)[key]);

      return {
        labels: pieLabels,
        datasets: [
          {
            data: pieData,
            backgroundColor: [
              'rgba(63, 81, 181, 0.7)',
              'rgba(156, 39, 176, 0.7)',
              'rgba(233, 30, 99, 0.7)',
              'rgba(66, 165, 245, 0.7)',
              'rgba(76, 175, 80, 0.7)',
            ],
            borderColor: [
              'rgba(63, 81, 181, 1)',
              'rgba(156, 39, 176, 1)',
              'rgba(233, 30, 99, 1)',
              'rgba(66, 165, 245, 1)',
              'rgba(76, 175, 80, 1)',
            ],
            borderWidth: 1,
          },
        ],
      };
    } else if (type === 'line' || type === 'bar') {
      // للمخطط الخطي أو الشريطي
      if (typeof values[0] === 'object' && !Array.isArray(values[0])) {
        // إذا كانت القيم عبارة عن كائنات، نقوم بإنشاء مجموعة بيانات لكل مفتاح
        const keys = Object.keys(values[0]);
        const datasets = keys.map((key, index) => {
          const colors = [
            'rgba(63, 81, 181, 0.7)',
            'rgba(156, 39, 176, 0.7)',
            'rgba(233, 30, 99, 0.7)',
            'rgba(66, 165, 245, 0.7)',
            'rgba(76, 175, 80, 0.7)',
          ];

          return {
            label: key,
            data: values.map(v => (v as any)[key]),
            backgroundColor: colors[index % colors.length],
            borderColor: colors[index % colors.length].replace('0.7', '1'),
            borderWidth: 2,
            tension: 0.4,
          };
        });

        return {
          labels,
          datasets,
        };
      } else {
        // إذا كانت القيم بسيطة
        return {
          labels,
          datasets: [
            {
              label: title,
              data: values,
              backgroundColor: 'rgba(63, 81, 181, 0.7)',
              borderColor: 'rgba(63, 81, 181, 1)',
              borderWidth: 2,
              tension: 0.4,
            },
          ],
        };
      }
    }

    // حالة افتراضية
    return {
      labels,
      datasets: [
        {
          label: title,
          data: values,
          backgroundColor: 'rgba(63, 81, 181, 0.7)',
          borderColor: 'rgba(63, 81, 181, 1)',
          borderWidth: 2,
        },
      ],
    };
  };

  // خيارات المخطط
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 16,
          family: 'Cairo',
        },
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }

            const value = context.parsed.y || context.parsed;

            // تنسيق القيمة حسب نوع البيانات
            if (dataKey === 'storage' && (subKey === 'total' || subKey === 'used' || !subKey)) {
              return label + formatSize(value);
            } else if (dataKey === 'hosting' && subKey === 'bandwidth') {
              return label + formatSize(value);
            } else if (dataKey === 'size') {
              return label + formatSize(value);
            }

            return label + (value !== null ? value.toLocaleString('ar-EG') : '0');
          }
        }
      }
    },
    scales: type !== 'pie' ? {
      x: {
        title: {
          display: !!xAxisLabel,
          text: xAxisLabel || '',
        },
      },
      y: {
        title: {
          display: !!yAxisLabel,
          text: yAxisLabel || '',
        },
        ticks: {
          callback: function(value: any) {
            // تنسيق القيمة حسب نوع البيانات
            if (dataKey === 'storage' && (subKey === 'total' || subKey === 'used' || !subKey)) {
              return formatSize(value);
            } else if (dataKey === 'hosting' && subKey === 'bandwidth') {
              return formatSize(value);
            } else if (dataKey === 'size') {
              return formatSize(value);
            }

            return value !== null ? value.toLocaleString('ar-EG') : '0';
          }
        }
      }
    } : undefined,
  };

  // عرض المخطط المناسب حسب النوع
  const renderChart = () => {
    const chartData = prepareChartData();

    switch (type) {
      case 'line':
        return <Line data={chartData} options={options} />;
      case 'bar':
        return <Bar data={chartData} options={options} />;
      case 'pie':
        return <Pie data={chartData} options={options} />;
      default:
        return <Line data={chartData} options={options} />;
    }
  };

  return (
    <Paper sx={{ p: 2, height: height + 50 }}>
      <Box sx={{ height }}>
        {renderChart()}
      </Box>
    </Paper>
  );
};

export default UsageChart;
